import * as React from 'react';
import * as moment from 'moment';

export const FMT = 'YYYY-MM-DD';

/**
 * Shared furniture for the report screens, so the summary and the per-caller
 * drill-down stay the same product rather than drifting apart.
 */

// Ranges people actually ask for, so "last month" is one click rather than
// two date pickers. `all` starts well before the earliest call on record.
export const PRESETS = [
  { key: 'last7', label: 'Last 7 days',
    range: () => [moment().subtract(6, 'days'), moment()] },
  { key: 'last30', label: 'Last 30 days',
    range: () => [moment().subtract(29, 'days'), moment()] },
  { key: 'thisMonth', label: 'This month',
    range: () => [moment().startOf('month'), moment()] },
  { key: 'lastMonth', label: 'Last month',
    range: () => [moment().subtract(1, 'month').startOf('month'),
                  moment().subtract(1, 'month').endOf('month')] },
  { key: 'thisYear', label: 'This year',
    range: () => [moment().startOf('year'), moment()] },
  { key: 'all', label: 'All time',
    range: () => [moment('2015-01-01'), moment()] },
];

// Not in the shared list: offered by the screens that ask for it, passed in
// through the `presets` arguments below.
export const TODAY: typeof PRESETS[0] = {
  key: 'today', label: 'Today', range: () => [moment(), moment()],
};

export const activePresetKey = (
  appliedFrom: string, appliedTo: string, presets: typeof PRESETS = PRESETS,
) => {
  const hit = presets.filter((p) => {
    const [a, b] = p.range();
    return a.format(FMT) === appliedFrom && b.format(FMT) === appliedTo;
  })[0];
  return hit ? hit.key : null;
};

export const RangePresets = (props: {
  activeKey: string;
  onPick: (preset: typeof PRESETS[0]) => void;
  presets?: typeof PRESETS;
}) => (
  <div className='cs-rep__presets'>
    <span className='cs-rep__presets-label'>Range</span>
    {(props.presets || PRESETS).map((p) => (
      <button
        key={p.key}
        type='button'
        className={`cs-chip${props.activeKey === p.key ? ' cs-chip--on' : ''}`}
        aria-pressed={props.activeKey === p.key}
        onClick={() => props.onPick(p)}
      >
        {p.label}
      </button>
    ))}
  </div>
);

export const statTile = (
  variant: string, label: string, value: React.ReactNode, sub: string,
) => (
  <div className={`cs-stat cs-stat--${variant}`} key={label}>
    <span className='cs-stat__label'>{label}</span>
    <span className='cs-stat__value'>{value}</span>
    <span className='cs-stat__sub'>{sub}</span>
  </div>
);

/** answered as a share of numbers actually dialled */
export const connectRate = (answered: number, noReply: number, wrong: number) => {
  const attempts = answered + noReply + wrong;
  return attempts ? Math.round((answered / attempts) * 100) : null;
};

export const rateMeter = (pct: number) => {
  if (pct === null) {
    return <span className='cs-rep__num cs-rep__num--zero'>—</span>;
  }
  const variant = pct >= 50 ? '' : pct >= 25 ? ' cs-rate__fill--mid' : ' cs-rate__fill--low';
  return (
    <span className='cs-rate' title={`${pct}% of dialled numbers were answered`}>
      <span className='cs-rate__track'>
        <span className={`cs-rate__fill${variant}`} style={{ width: `${pct}%` }} />
      </span>
      <span className='cs-rate__pct'>{pct}%</span>
    </span>
  );
};

export const groupNum = (n: number) => (n || 0).toLocaleString('en-IN');

/**
 * The API hands durations back pre-formatted as "1 Minutes 12 Seconds", which
 * is both verbose in a table and useless for arithmetic. Parse it back to
 * seconds so it can be re-rendered compactly.
 */
export const durationSeconds = (formatted: string): number => {
  if (!formatted) { return 0; }
  const m = /(\d+)\s*Minutes?/i.exec(formatted);
  const s = /(\d+)\s*Seconds?/i.exec(formatted);
  return (m ? parseInt(m[1], 10) * 60 : 0) + (s ? parseInt(s[1], 10) : 0);
};

export const shortDuration = (seconds: number): string => {
  if (!seconds) { return '0s'; }
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins ? `${mins}m ${secs}s` : `${secs}s`;
};

/**
 * sha-el's Table exposes no row-props hook, so the stacked-card labels and the
 * row click target are wired onto the rendered DOM.
 *
 * MUST be driven from an inline ref callback, not componentDidUpdate: these
 * tables render inside GraphqlQuery's render callback, and GraphqlQuery holds
 * its own state -- when the response arrives it re-renders itself without
 * re-rendering the page component, so the parent's lifecycle hooks never fire
 * for the render that first produces rows. React also only re-invokes a ref
 * callback when its identity changes, so a bound method would fire once on
 * mount and leave rows added by pagination undecorated.
 */
export const decorateRows = (root: HTMLElement, opts?: { clickable?: boolean }) => {
  if (!root) { return; }
  const headers = Array.prototype.slice
    .call(root.querySelectorAll('thead th'))
    .map((th: HTMLElement) => th.textContent || '');
  Array.prototype.forEach.call(root.querySelectorAll('tbody tr'), (tr: HTMLElement) => {
    if (opts && opts.clickable) { tr.classList.add('cs-rep__row'); }
    Array.prototype.forEach.call(tr.querySelectorAll('td'), (td: HTMLElement, i: number) => {
      if (headers[i]) { td.setAttribute('data-label', headers[i]); }
    });
  });
};

/** index of the clicked row within a decorated table, or -1 */
export const clickedRowIndex = (root: HTMLElement, target: HTMLElement): number => {
  if (!root || !target || !target.closest) { return -1; }
  if (target.closest('a')) { return -1; }          // let real links win
  const tr = target.closest('tbody tr');
  if (!tr) { return -1; }
  return Array.prototype.indexOf.call(root.querySelectorAll('tbody tr'), tr);
};
